import { useCallback } from "react";
import { sha256 } from "js-sha256";
import { EVENTS_URL } from "../config";
import { RequestConfigType } from "./types/request-config";
import useFetch from "../use-fetch";
import wrapperFetchJsonResponse from "../wrapper-fetch-json-response";
import {
    BiqQueryPropertyType,
    FacebookPropertyType,
    TrackingiqQueryPEventFields,
} from "./types/EventsEnum";
import useAuthTokens from "~/services/auth/use-auth-tokens";
import useUserId from "~/hooks/useUserId";

type EventPropertyValue = string | number | boolean | null | undefined;

type WebEventProperties = Partial<Record<TrackingiqQueryPEventFields, EventPropertyValue>>;

type FacebookEventProperties = {
    [K in TrackingiqQueryPEventFields]?: K extends TrackingiqQueryPEventFields.UserData
        ? { external_id?: string; [key: string]: unknown }
        : EventPropertyValue;
};

export type EventRequest =
    | {
          type: BiqQueryPropertyType.WEB_EVENT;
          properties: WebEventProperties;
      }
    | {
          type: FacebookPropertyType.FACEBOOK_EVENT;
          properties: FacebookEventProperties;
      };

export type EventResponse = void;

export function useUnauthorizedEventService() {
    const fetchBase = useFetch();
    const { tokensInfoRef } = useAuthTokens();
    const isAuth = tokensInfoRef.current?.token;
    const withFetch = isAuth ? fetchBase : fetch;
    const { userId } = useUserId();

    return useCallback(
        (data: EventRequest, requestConfig?: RequestConfigType) => {
            if (data.type === BiqQueryPropertyType.WEB_EVENT && !data.properties.user_id) {
                data.properties.user_id = userId;
            }

            if (data.type === FacebookPropertyType.FACEBOOK_EVENT) {
                if (!data.properties.user_data) {
                    data.properties.user_data = {};
                }
                if (!data.properties.user_data.external_id) {
                    data.properties.user_data.external_id = sha256(userId || "");
                }
            }

            return withFetch(
                `${EVENTS_URL}/sendUnauthorizedEvent`,
                {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                        "x-custom-lang": "en",
                    },
                    body: JSON.stringify(data),
                    ...requestConfig,
                    // Lets the request finish after the page is closed or navigated away.
                    keepalive: true,
                }
            ).then(wrapperFetchJsonResponse<EventResponse>);
        },
        [withFetch, userId]
    );
}
